import React, { useState, useEffect, useRef, useCallback, useMemo } from 'react';
import moment from 'moment';

// Constants - 12 hours for production, 30 minutes for development
const FORCE_RELOAD_INTERVAL_SECONDS = process.env.NODE_ENV === 'production' ? 12 * 60 * 60 : 30 * 60; // 12 hours for production, 30 minutes for development
const GRACE_PERIOD_SECONDS = 60; // 1 minute grace period when returning to expired timer
const WARNING_THRESHOLD_SECONDS = 60; // 1 minute warning threshold
const LOCAL_STORAGE_KEY = 'sbgo_last_refresh_timestamp';

// Types
interface TimerState {
  secondsRemaining: number | null;
  showBanner: boolean;
  isPaused: boolean;
}

const ForceReloadBanner: React.FC = () => {
  const [timerState, setTimerState] = useState<TimerState>({
    secondsRemaining: null,
    showBanner: false,
    isPaused: false,
  });

  const intervalRef = useRef<NodeJS.Timeout | null>(null);

  const getTimeRemaining = useCallback((): number | null => {
    const lastRefresh = localStorage.getItem(LOCAL_STORAGE_KEY);
    if (!lastRefresh) return null;

    const now = moment();
    const lastRefreshTime = moment(lastRefresh);
    const secondsSinceLastRefresh = now.diff(lastRefreshTime, 'seconds');
    return Math.max(0, FORCE_RELOAD_INTERVAL_SECONDS - secondsSinceLastRefresh);
  }, []);

  const updateLocalStorage = useCallback((timestamp?: moment.Moment) => {
    localStorage.setItem(LOCAL_STORAGE_KEY, timestamp?.toISOString() || moment().toISOString());
  }, []);

  const stopTimer = useCallback(() => {
    if (intervalRef.current) {
      clearInterval(intervalRef.current);
      intervalRef.current = null;
    }
  }, []);

  const startTimer = useCallback(() => {
    if (!intervalRef.current) {
      const updateTimer = () => {
        const remaining = getTimeRemaining();

        if (remaining === null) {
          updateLocalStorage();
          setTimerState((prev) => ({ ...prev, showBanner: false }));
          return;
        }

        if (remaining <= WARNING_THRESHOLD_SECONDS && remaining > 0) {
          setTimerState((prev) => ({
            ...prev,
            secondsRemaining: remaining,
            showBanner: true,
          }));
        } else if (remaining <= 0) {
          updateLocalStorage();
          window.location.reload();
        } else {
          setTimerState((prev) => ({ ...prev, showBanner: false }));
        }
      };

      updateTimer(); // Initial call
      intervalRef.current = setInterval(updateTimer, 1000);
    }
  }, [getTimeRemaining, updateLocalStorage]);

  const restartTimer = useCallback(() => {
    updateLocalStorage();
    setTimerState((prev) => ({ ...prev, showBanner: false }));
    stopTimer();
    startTimer();
  }, [updateLocalStorage, stopTimer, startTimer]);

  const handleVisibilityChange = useCallback(() => {
    if (document.hidden) {
      setTimerState((prev) => ({ ...prev, isPaused: true }));
      stopTimer();
    } else {
      setTimerState((prev) => ({ ...prev, isPaused: false }));
      const remaining = getTimeRemaining();

      if (remaining !== null && remaining <= 0) {
        // Back-calculate timestamp to give grace period
        const backCalculatedTime = moment().subtract(FORCE_RELOAD_INTERVAL_SECONDS - GRACE_PERIOD_SECONDS, 'seconds');
        updateLocalStorage(backCalculatedTime);
      }
      startTimer();
    }
  }, [getTimeRemaining, updateLocalStorage, stopTimer, startTimer]);

  // Check if page was refreshed
  // https://developer.mozilla.org/en-US/docs/Web/API/PerformanceNavigationTiming/type
  const isPageRefresh = useMemo(() => {
    return (
      performance.navigation.type === 1 ||
      (performance.getEntriesByType('navigation')[0] as PerformanceNavigationTiming)?.type === 'reload'
    );
  }, []);

  const bannerStyles = useMemo(
    () => ({
      position: 'fixed' as const,
      top: 0,
      left: 0,
      right: 0,
      zIndex: 1000,
      backgroundColor: '#fff7e6',
      borderTop: '1px solid rgb(252, 217, 159)',
      padding: '12px 16px',
      textAlign: 'center' as const,
      fontSize: '14px',
      color: '#d46b08',
      boxShadow: '0 -2px 8px rgba(0, 0, 0, 0.1)',
    }),
    []
  );

  useEffect(() => {
    if (isPageRefresh) {
      restartTimer();
    } else {
      startTimer();
    }

    // Monitor visibility change
    document.addEventListener('visibilitychange', handleVisibilityChange);

    return () => {
      stopTimer();
      document.removeEventListener('visibilitychange', handleVisibilityChange);
    };
  }, [isPageRefresh, restartTimer, startTimer, handleVisibilityChange, stopTimer]);

  if (!timerState.showBanner) {
    return null;
  }

  return (
    <div style={bannerStyles}>
      🔄 Reloading in <strong>{timerState.secondsRemaining}s</strong> to ensure you have the latest updates
    </div>
  );
};

export default ForceReloadBanner;
